import { Box, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { _postFundUploadRequest, _putFunds, FundFileUploadRequest } from '@/api/fund/mutations';
import { _getSingleFundOptions, FUND_QUERY_KEYS } from '@/api/fund/queries';
import { Dropzone, FileItem, FileItemFrame } from '@/design-system';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { mapToPayload } from '@/pages/setup-fund/utils/map-step-form-to-payload';
import { Fund } from '@/pages/setup-fund/utils/type';
import { CustomButton } from '@/shared/components/custom-button';
import CustomModal from '@/shared/components/custom-modal';
import { CustomSelect } from '@/shared/components/custom-select';
import { CustomTextField } from '@/shared/components/custom-text-field';
import {
  FILE_PROPAGATION_WAIT_MS,
  FUND_DOCUMENT_ADD_DENIED_MESSAGE,
  FUND_DOCUMENT_CATEGORY_OPTIONS,
  FUND_DOCUMENT_MAX_FILE_SIZE_BYTES,
  FUND_DOCUMENT_NOTES_MAX_LENGTH,
  FundDocumentCategoryKey,
} from '@/shared/constants/fund-documents';
import { Permission, RequirePermissionInline, useCan } from '@/shared/modules/permissions';
import { emitToast } from '@/utils/emit-toast';
import { sleep } from '@/utils/sleep';
import { uploadToS3 } from '@/utils/upload-to-s3';
import { getFileSizeError } from '@/utils/validate-file-size';

export type AddFundDocumentModalProps = {
  fundId: string;
  documents: Pick<
    Fund,
    'offeringDocuments' | 'subscriptionDocuments' | 'riskDisclosureDocuments' | 'marketingDocuments'
  >;
};

type ModalCategoryKey = Exclude<FundDocumentCategoryKey, 'audits'>;

export const AddFundDocumentModal = () => {
  const { open, handleClose, props } = useGlobalModal('ADD_FUND_DOCUMENT_MODAL');
  const canManageFund = useCan(Permission.FundManage);
  const queryClient = useQueryClient();

  const [category, setCategory] = useState<ModalCategoryKey | ''>('');
  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [isWaitingForPropagation, setIsWaitingForPropagation] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCategory('');
    setFile(null);
    setNotes('');
    setIsWaitingForPropagation(false);
  }, [open]);

  const { mutateAsync: createUploadRequest, isPending: isCreatingRequest } = useMutation({
    mutationFn: _postFundUploadRequest,
  });

  const { mutateAsync: updateFund, isPending: isUpdatingFund } = useMutation({
    mutationFn: _putFunds,
  });

  const { data: fund, isLoading: isFundLoading } = useQuery({
    ..._getSingleFundOptions(props?.fundId || ''),
    enabled: open && !!props?.fundId && canManageFund,
    staleTime: 0,
  });

  const isSubmitting = isCreatingRequest || isUpdatingFund || isWaitingForPropagation;
  const canUploadFile = Boolean(category) && !isSubmitting;
  const canSubmit =
    Boolean(category) &&
    Boolean(file) &&
    !isSubmitting &&
    Boolean(props?.fundId) &&
    !isFundLoading &&
    Boolean(fund);

  const selectedCategoryLabel = useMemo(() => {
    const found = FUND_DOCUMENT_CATEGORY_OPTIONS.find((o) => o.value === category);
    return found?.label ?? '';
  }, [category]);

  const onFilePicked = (f: File | null | undefined) => {
    if (!f) return;
    const sizeError = getFileSizeError(f, FUND_DOCUMENT_MAX_FILE_SIZE_BYTES);
    if (sizeError) {
      emitToast({ type: 'error', title: 'File too large', message: sizeError });
      return;
    }
    setFile(f);
  };

  const handleSubmit = async () => {
    if (!props?.fundId) return;
    if (!category) return;
    if (!file) return;

    try {
      const contentType = file.type || 'application/pdf';

      const uploadReq = await createUploadRequest({
        fundId: props.fundId,
        filename: file.name,
        contentType,
        contentEncoding: 'gzip',
        category: 'document',
        file,
      } satisfies FundFileUploadRequest);

      await uploadToS3({
        uploadUrl: uploadReq.uploadUrl,
        uploadHeaders: uploadReq.uploadHeaders as Record<string, string>,
        file,
        fileId: uploadReq.fileId,
        compress: true,
      });

      const baseFund = fund ?? ({} as Fund);
      const payload = mapToPayload(baseFund);

      const currentSection = (
        payload as unknown as Record<string, { notes: string; documentIds: string[] }>
      )[category] ??
        props.documents?.[category] ?? { notes: '', documentIds: [] };
      const nextIds = Array.from(
        new Set([...(currentSection.documentIds || []).filter(Boolean), uploadReq.fileId]),
      );
      const nextNotes = notes.trim() ? notes : currentSection.notes || '';

      await updateFund({
        id: props.fundId,
        body: {
          ...payload,
          [category]: { notes: nextNotes, documentIds: nextIds },
        },
      });

      setIsWaitingForPropagation(true);
      await sleep(FILE_PROPAGATION_WAIT_MS);

      await queryClient.invalidateQueries({
        queryKey: [FUND_QUERY_KEYS.funds, props.fundId],
        refetchType: 'all',
      });

      handleClose();
    } finally {
      setIsWaitingForPropagation(false);
    }
  };

  return (
    <CustomModal
      open={open}
      onClose={handleClose}
      withCloseButton
      title="Add document"
      sx={{
        maxWidth: 720,
        width: 'calc(100vw - 32px)',
      }}
    >
      <Box mt="16px">
        <Divider sx={{ borderColor: '#39455B', marginBottom: '24px' }} />
        <RequirePermissionInline
          permission={Permission.FundManage}
          deniedMessage={FUND_DOCUMENT_ADD_DENIED_MESSAGE}
        >
          <Stack gap="24px" flexDirection="column">
            <CustomSelect
              displayEmpty
              value={category}
              options={
                FUND_DOCUMENT_CATEGORY_OPTIONS.filter((o) => o.value !== 'audits') as unknown as {
                  label: string;
                  value: string;
                }[]
              }
              handleChange={(e: SelectChangeEvent<unknown>) => {
                const val = String(e.target.value || '') as ModalCategoryKey | '';
                setCategory(val);
                setFile(null);
              }}
              renderValue={(selected) => {
                const v = String(selected || '');
                if (!v) return <Typography variant="body2">Select document category</Typography>;
                return <Typography variant="body2">{selectedCategoryLabel}</Typography>;
              }}
              sx={{
                height: 44,
                borderRadius: '6px !important',
                backgroundColor: '#191D26 !important',
                border: '1px solid #161D29 !important',
                '&:hover': { borderColor: '#39CA88 !important' },
              }}
            />

            {!file ? (
              <Dropzone
                subject="document"
                accept=".pdf,application/pdf"
                acceptLabel="PDF"
                inputLabel="Upload file"
                disabled={!canUploadFile}
                onFilesSelected={([picked]) => onFilePicked(picked)}
              />
            ) : (
              <FileItemFrame>
                <FileItem
                  file={{
                    id: 'new-document',
                    name: file.name,
                    size: file.size,
                    contentType: file.type,
                    status: 'uploaded',
                  }}
                  onRemove={() => setFile(null)}
                />
              </FileItemFrame>
            )}

            <CustomTextField
              label="Notes and Explanations"
              labelColor="text.secondary"
              bigLabel
              fullWidth
              multiline
              rows={4}
              placeholder="Enter Notes and Explanations"
              value={notes}
              inputProps={{ maxLength: FUND_DOCUMENT_NOTES_MAX_LENGTH }}
              onChange={(e) => setNotes(e.target.value)}
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: !canUploadFile ? 'transparent' : '#1A212A',
                  borderColor: 'rgba(255, 255, 255, 0.15)',
                },
              }}
            />

            <CustomButton
              variant="contained"
              fullWidth
              disabled={!canSubmit}
              onClick={() => void handleSubmit()}
              sx={{
                height: 44,
                borderRadius: '10px',
                background: '#39CA88',
                ':disabled': { opacity: 0.3 },
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={16} sx={{ color: '#fff' }} />
              ) : (
                'Add document'
              )}
            </CustomButton>
          </Stack>
        </RequirePermissionInline>
      </Box>
    </CustomModal>
  );
};
